import { useState, useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import { useSEO, getWebPageSchema } from '../../utils/seo';

export default function AboutPage() {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useSEO({
    title: 'About Million Dollar Contractors | Boca Raton Digital Marketing Agency for Contractors',
    description: 'Learn about Million Dollar Contractors, a Boca Raton-based digital marketing agency specializing in home service businesses. We help electrical, HVAC, plumbing, roofing, and landscaping contractors dominate their markets with proven strategies.',
    keywords: 'about million dollar contractors, Boca Raton marketing agency, contractor marketing experts, home service marketing specialists',
    canonical: '/about',
    schema: getWebPageSchema(
      'About Million Dollar Contractors',
      'Learn about our mission to empower home service businesses with innovative digital marketing strategies.',
      '/about'
    )
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5t3emtvmu9mcvg4n500', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const stats = [
    { number: '500+', label: 'Clients Served' },
    { number: '98%', label: 'Client Retention' },
    { number: '15+', label: 'Years Experience' },
    { number: '$50M+', label: 'Revenue Generated' }
  ];

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'We stay ahead of digital trends to deliver cutting-edge solutions that keep your business competitive.'
    },
    {
      icon: 'ri-team-line',
      title: 'Partnership',
      description: 'Your success is our success. We work as an extension of your team, dedicated to achieving your goals.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Results-Driven',
      description: 'Every strategy we implement is focused on delivering measurable ROI and sustainable growth.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Integrity',
      description: 'Transparency and honesty guide everything we do. We build trust through consistent, ethical practices.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-burgundy-950 via-burgundy-900 to-navy-950 overflow-hidden">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              About Million Dollar Contractors
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 mb-8 leading-relaxed">
              Based in Boca Raton, we're a full-service digital marketing agency dedicated to transforming home service businesses into industry leaders.
            </p>
            <button
              onClick={scrollToContact}
              className="inline-flex items-center gap-2 bg-red-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-red-600 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Let's Work Together
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl lg:text-6xl font-bold text-red-500 mb-2">
                  {stat.number}
                </div>
                <div className="text-lg text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Million Dollar Contractors was born from a simple observation: home service businesses are the backbone of our communities, yet many struggle to reach their full potential in the digital age.
                </p>
                <p>
                  Founded in Boca Raton, Florida, we set out to change that. Our team of digital marketing experts specializes exclusively in helping contractors, plumbers, electricians, HVAC technicians, and other home service professionals dominate their local markets.
                </p>
                <p>
                  We understand the unique challenges you face—seasonal fluctuations, local competition, and the need for immediate results. That's why we've developed proven strategies that generate qualified leads, build your reputation, and turn your business into a household name in your service area.
                </p>
                <p>
                  Today, we're proud to partner with hundreds of home service businesses across the country, helping them achieve consistent growth and financial success. Our clients don't just survive—they thrive, becoming the go-to choice in their communities.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <div className="w-full h-[500px]">
                  <img
                    src="https://public.readdy.ai/ai/img_res/49fba65fd116e8bfedf0ca01a9c8083f.jpg"
                    alt="Agency team collaborating in modern office"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
              <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-red-500/10 rounded-2xl -z-10"></div>
              <div className="absolute -top-8 -right-8 w-64 h-64 bg-gray-900/10 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-2xl p-10 shadow-lg border-t-4 border-red-500">
              <div className="w-16 h-16 bg-red-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-compass-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                To empower home service businesses with innovative digital marketing strategies that drive measurable growth, enhance their reputation, and establish them as the dominant force in their local markets. We're committed to delivering exceptional results that transform businesses and change lives.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 shadow-lg border-t-4 border-gray-900">
              <div className="w-16 h-16 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                To become the most trusted digital marketing partner for home service businesses nationwide, recognized for our expertise, integrity, and unwavering commitment to client success. We envision a future where every contractor we work with achieves financial freedom and industry leadership.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              These principles guide every decision we make and every strategy we implement for our clients.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <i className={`${value.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <div className="w-full h-[500px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=diverse%20marketing%20team%20working%20together%20at%20modern%20office%20desk%20collaborative%20workspace%20professionals%20analyzing%20data%20on%20computer%20screens%20laptops%20tablets%20business%20casual%20attire%20bright%20natural%20lighting%20clean%20contemporary%20office%20environment%20neutral%20background%20focused%20teamwork%20professional%20atmosphere&width=800&height=600&seq=about-team-workspace-mdc-002&orientation=landscape"
                    alt="Agency team brainstorming strategies"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8">
                What Sets Us Apart
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                    <i className="ri-focus-3-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Industry Specialization</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We exclusively serve home service businesses, giving us deep insights into your industry, challenges, and opportunities that generalist agencies simply don't have.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                    <i className="ri-bar-chart-box-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Proven Track Record</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Our strategies have generated over $50 million in revenue for our clients. We don't just promise results—we deliver them consistently.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Dedicated Support</h3>
                    <p className="text-gray-700 leading-relaxed">
                      You'll have a dedicated account manager who knows your business inside and out, providing personalized attention and strategic guidance.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                    <i className="ri-tools-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Cutting-Edge Technology</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We leverage the latest AI tools, automation, and analytics platforms to maximize your ROI and stay ahead of the competition.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Team in Action
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Experienced professionals dedicated to your success.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <div className="w-full h-[400px]">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20business%20team%20presentation%20meeting%20group%20of%20diverse%20marketing%20professionals%20standing%20around%20large%20display%20screen%20showing%20analytics%20data%20charts%20modern%20conference%20room%20collaborative%20discussion%20bright%20natural%20lighting%20clean%20neutral%20background%20business%20casual%20attire%20professional%20atmosphere%20contemporary%20office&width=800&height=600&seq=about-team-presentation-mdc-003&orientation=landscape"
                  alt="Team presentation and strategy session"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="text-2xl font-bold text-white mb-2">Strategic Planning</h3>
                <p className="text-gray-200">Our team develops data-driven strategies tailored to your business goals.</p>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <div className="w-full h-[400px]">
                <img
                  src="https://mediacomponents.com/wp-content/uploads/2026/01/media-components-quality-team-1.webp"
                  alt="Team collaboration and campaign analysis"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="text-2xl font-bold text-white mb-2">Campaign Execution</h3>
                <p className="text-gray-200">We execute and optimize campaigns to deliver maximum ROI for your business.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Based in Boca Raton, Serving Nationwide
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Our headquarters in Boca Raton, Florida, serves as the hub for our nationwide operations. While we're proud to call South Florida home, our digital expertise knows no boundaries.
                </p>
                <p>
                  We work with home service businesses across the United States, from bustling metropolitan areas to growing suburban markets. Our team combines local market knowledge with national best practices to deliver strategies that work in your specific service area.
                </p>
                <p>
                  Whether you're looking to dominate your local market or expand into new territories, we have the experience and resources to make it happen.
                </p>
              </div>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Boca Raton, Florida</p>
                    <p className="text-gray-600">Headquarters</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">(561) 555-0100</p>
                    <p className="text-gray-600">Call us today</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">info@milliondollarcontractors.com</p>
                    <p className="text-gray-600">Email us</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[600px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3571.5!2d-80.0831!3d26.3586!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDIxJzMxLjAiTiA4MMKwMDQnNTkuMiJX!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Million Dollar Contractors Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-burgundy-950 via-burgundy-900 to-navy-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-red-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Let's discuss how we can help you dominate your market and achieve the growth you deserve.
          </p>
          <button
            onClick={scrollToContact}
            className="inline-flex items-center gap-2 bg-red-500 text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-red-600 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Schedule Your Free Consultation
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Column - Contact Info */}
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Get in Touch
              </h2>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                Ready to take your home service business to the next level? Fill out the form and our team will get back to you within 24 hours.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Phone</h3>
                    <p className="text-gray-600">(561) 555-0100</p>
                    <p className="text-sm text-gray-500 mt-1">Mon-Fri 9am-6pm EST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600">info@milliondollarcontractors.com</p>
                    <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Office</h3>
                    <p className="text-gray-600">Boca Raton, Florida</p>
                    <p className="text-sm text-gray-500 mt-1">Serving contractors nationwide</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-gradient-to-br from-red-50 to-gray-50 rounded-2xl border border-red-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Quick Response Time</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We understand your time is valuable. Our team typically responds to all inquiries within 24 hours, often much sooner.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="bg-gray-50 rounded-2xl p-8 lg:p-10 shadow-lg border-t-4 border-red-500">
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-base"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-base"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-base"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-base"
                    placeholder="Your Company Name"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none text-base"
                    placeholder="Tell us about your business and how we can help..."
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-2">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  {formStatus === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line text-xl animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-send-plane-line text-xl"></i>
                    </>
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-2xl text-green-600 flex-shrink-0"></i>
                    <div>
                      <p className="font-semibold text-green-900">Message sent successfully!</p>
                      <p className="text-sm text-green-700 mt-1">We'll get back to you within 24 hours.</p>
                    </div>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <i className="ri-error-warning-line text-2xl text-red-600 flex-shrink-0"></i>
                    <div>
                      <p className="font-semibold text-red-900">Something went wrong</p>
                      <p className="text-sm text-red-700 mt-1">Please try again or call us directly.</p>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
